import React, { useEffect, useMemo, useState } from 'react';
import StaffService from '../../services/StaffService';
import RoleService from '../../services/RoleService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const Staff = () => {
    const [staffList, setStaffList] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        staffCode: '',
        firstName: '',
        lastName: '',
        mobileNo: '',
        emailId: '',
        gender: 'Male',
        roleId: 1,
        doj: '',
        dor: '',
        address: '',
        landmark: '',
        state: '',
        city: '',
        zip: '',
        status: 'Active',
        isActive: 1
    });
    const filteredStaffList = useMemo(() => filterList(staffList, searchText, ['staffCode', 'firstName', 'lastName', 'emailId', 'mobileNo', 'status']), [staffList, searchText]);

    useEffect(() => {
        fetchDropdownData();
        fetchData();
    }, []);

    useEffect(() => {
        fetchData();
    }, [currentPage]);

    const fetchDropdownData = async () => {
        try {
            const rolesRes = await RoleService.getAll(1, 100);
            setRoles(rolesRes.data || []);
        } catch (err) {
            utility.showError('Failed to load roles: ' + err.message);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await StaffService.getAll(currentPage, itemsPerPage);
            setStaffList(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (staff = null) => {
        if (staff) {
            setEditMode(true);
            setFormData({
                id: staff.id,
                staffCode: staff.staffCode,
                firstName: staff.firstName || '',
                lastName: staff.lastName || '',
                mobileNo: staff.mobileNo || '',
                emailId: staff.emailId || '',
                gender: staff.gender || 'Male',
                roleId: staff.roleId || 1,
                doj: staff.doj ? staff.doj.split('T')[0] : '',
                dor: staff.dor ? staff.dor.split('T')[0] : '',
                address: staff.address || '',
                landmark: staff.landmark || '',
                state: staff.state || '',
                city: staff.city || '',
                zip: staff.zip || '',
                status: staff.status || 'Active',
                isActive: staff.isActive
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                staffCode: '',
                firstName: '',
                lastName: '',
                mobileNo: '',
                emailId: '',
                gender: 'Male',
                roleId: 1,
                doj: '',
                dor: '',
                address: '',
                landmark: '',
                state: '',
                city: '',
                zip: '',
                status: 'Active',
                isActive: 1
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: (name === 'isActive' || name === 'roleId') ? parseInt(value) : value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await StaffService.update(formData.id, formData);
                utility.showSuccess('Staff updated successfully');
            } else {
                await StaffService.create(formData);
                utility.showSuccess('Staff created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async (staff) => {
        try {
            const newStatus = staff.isActive ? 0 : 1;
            await StaffService.updateStatus(staff.id, newStatus);
            utility.showSuccess(`Staff status ${newStatus ? 'activated' : 'deactivated'} successfully`);
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        }
    };

    const handlePageClick = (event) => {
        const selectedPage = event.selected + 1;
        setCurrentPage(selectedPage);
    };

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Staff
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Staff</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            {/* Error alert removed in favor of professional toasts */}
                            <div className="px-3">
                                <FilterBar
                                    value={searchText}
                                    onChange={setSearchText}
                                    onClear={() => setSearchText('')}
                                    placeholder="Search staff..."
                                />
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Staff Name</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Code / Contact</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Role</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredStaffList.length === 0 ? (
                                                <tr><td colSpan="5" className="text-center py-4">No staff found.</td></tr>
                                            ) : (
                                                filteredStaffList.map((staff) => (
                                                    <tr key={staff.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{staff.firstName} {staff.lastName}</h6>
                                                                    <p className="text-xs text-secondary mb-0">{staff.emailId || 'No Email'}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{staff.staffCode}</p>
                                                            <p className="text-xs text-secondary mb-0">{staff.mobileNo || 'N/A'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{staff.roleName || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${staff.isActive ? 'success' : 'secondary'} cursor-pointer`}
                                                                onClick={() => handleToggleStatus(staff)}
                                                            >
                                                                {staff.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(staff)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="5">
                                                    <ReactPaginate
                                                        forcePage={currentPage - 1}
                                                        breakLabel="..."
                                                        nextLabel=">"
                                                        onPageChange={handlePageClick}
                                                        pageRangeDisplayed={3}
                                                        marginPagesDisplayed={2}
                                                        pageCount={pageCount}
                                                        previousLabel="<"
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-xl">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title font-weight-normal">{editMode ? 'Edit Staff' : 'Add New Staff'}</h5>
                                <button type="button" className="btn-close text-dark" onClick={handleCloseModal}>
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body row">
                                    <div className="col-md-3 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Staff Code</label>
                                            <input type="text" name="staffCode" className="form-control" value={formData.staffCode} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>First Name</label>
                                            <input type="text" name="firstName" className="form-control" value={formData.firstName} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-5 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Last Name</label>
                                            <input type="text" name="lastName" className="form-control" value={formData.lastName} onChange={handleInputChange} />
                                        </div>
                                    </div>

                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Email ID</label>
                                            <input type="email" name="emailId" className="form-control" value={formData.emailId} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Mobile No</label>
                                            <input type="text" name="mobileNo" className="form-control" value={formData.mobileNo} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Gender</label>
                                            <select name="gender" className="form-control" value={formData.gender} onChange={handleInputChange}>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Role *</label>
                                            <select
                                                name="roleId"
                                                className="form-control"
                                                value={formData.roleId}
                                                onChange={handleInputChange}
                                                required
                                            >
                                                <option value="">Select Role</option>
                                                {roles.map(role => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.roleName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Date of Joining</label>
                                            <input type="date" name="doj" className="form-control" value={formData.doj} onChange={handleInputChange} required />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Date of Resignation</label>
                                            <input type="date" name="dor" className="form-control" value={formData.dor} onChange={handleInputChange} />
                                        </div>
                                    </div>

                                    <div className="col-md-12 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Address</label>
                                            <input type="text" name="address" className="form-control" value={formData.address} onChange={handleInputChange} />
                                        </div>
                                    </div>

                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>City</label>
                                            <input type="text" name="city" className="form-control" value={formData.city} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>State</label>
                                            <input type="text" name="state" className="form-control" value={formData.state} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Zip</label>
                                            <input type="text" name="zip" className="form-control" value={formData.zip} onChange={handleInputChange} />
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Status Label</label>
                                            <input type="text" name="status" className="form-control" value={formData.status} onChange={handleInputChange} />
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="input-group input-group-static">
                                            <label>Is Active</label>
                                            <select name="isActive" className="form-control" value={formData.isActive} onChange={handleInputChange}>
                                                <option value={1}>Active</option>
                                                <option value={0}>Inactive</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0">
                                    <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                    <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {editMode ? 'Updating...' : 'Saving...'}
                                            </>
                                        ) : (
                                            editMode ? 'Update' : 'Save'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Staff;
